import type { GeoBounds, GeoPoint } from "./coordinates.ts";

/** Endpoint/client contract for streamed OSM road semantics. */
export const WORLD_REGION_SCHEMA_VERSION = 3 as const;

/** String IDs avoid precision loss and remain stable across JSON and workers. */
export type OsmElementId = string;
export type OsmTags = Readonly<Record<string, string>>;

export interface OsmNode {
  readonly type: "node";
  readonly id: OsmElementId;
  readonly point: GeoPoint;
  readonly tags: OsmTags;
}

export interface OsmWay {
  readonly type: "way";
  readonly id: OsmElementId;
  readonly nodeIds: readonly OsmElementId[];
  readonly tags: OsmTags;
}

export type OsmRelationMemberType = "node" | "way" | "relation";

export interface OsmRelationMember {
  readonly type: OsmRelationMemberType;
  readonly ref: OsmElementId;
  readonly role: string;
  /** Original OSM member ref when a transported way was split into local parts. */
  readonly sourceRef?: OsmElementId;
}

export interface OsmRelation {
  readonly type: "relation";
  readonly id: OsmElementId;
  readonly members: readonly OsmRelationMember[];
  readonly tags: OsmTags;
}

export interface OsmSourceProvenance {
  readonly provider: string;
  readonly dataset?: string;
  readonly revision?: string;
  readonly fetchedAtIso?: string;
  readonly licence?: string;
  readonly bounds?: GeoBounds;
}

/** Raw OSM topology after transport-format decoding, before road interpretation. */
export interface OsmDataset {
  readonly nodes: readonly OsmNode[];
  /**
   * Non-drivable linework retained solely to explain vertical road crossings.
   * These ways must never enter the road graph; they provide railway and
   * waterway evidence to the civil interpreter.
   *
   * Undefined means the source predates crossing-aware transport, so bridge
   * clearance must fail closed. An explicit empty collection means the source
   * was queried and no non-road crossing was found.
   */
  readonly crossingWays?: readonly OsmWay[];
  readonly ways: readonly OsmWay[];
  readonly relations: readonly OsmRelation[];
  readonly provenance: OsmSourceProvenance;
}

export interface OsmJsonNode {
  readonly type: "node";
  readonly id: string | number;
  readonly lon: number;
  readonly lat: number;
  readonly tags?: Readonly<Record<string, unknown>>;
}

export interface OsmJsonWay {
  readonly type: "way";
  readonly id: string | number;
  readonly nodes: readonly (string | number)[];
  readonly tags?: Readonly<Record<string, unknown>>;
}

export interface OsmJsonRelationMember {
  readonly type: OsmRelationMemberType;
  readonly ref: string | number;
  readonly role?: string;
}

export interface OsmJsonRelation {
  readonly type: "relation";
  readonly id: string | number;
  readonly members: readonly OsmJsonRelationMember[];
  readonly tags?: Readonly<Record<string, unknown>>;
}

export type OsmJsonElement = OsmJsonNode | OsmJsonWay | OsmJsonRelation;

/** Converts an Overpass-style JSON element list into the transport-neutral model. */
export function osmDatasetFromJsonElements(
  elements: readonly OsmJsonElement[],
  provenance: OsmSourceProvenance,
): OsmDataset {
  const nodes: OsmNode[] = [];
  const ways: OsmWay[] = [];
  const relations: OsmRelation[] = [];

  for (const element of elements) {
    if (element.type === "node") {
      nodes.push({
        type: "node",
        id: normaliseOsmElementId(element.id),
        point: { lon: element.lon, lat: element.lat },
        tags: normaliseOsmTags(element.tags),
      });
      continue;
    }
    if (element.type === "way") {
      ways.push({
        type: "way",
        id: normaliseOsmElementId(element.id),
        nodeIds: element.nodes.map(normaliseOsmElementId),
        tags: normaliseOsmTags(element.tags),
      });
      continue;
    }
    relations.push({
      type: "relation",
      id: normaliseOsmElementId(element.id),
      members: element.members.map((member) => ({
        type: member.type,
        ref: normaliseOsmElementId(member.ref),
        role: member.role ?? "",
      })),
      tags: normaliseOsmTags(element.tags),
    });
  }

  return { nodes, ways, relations, provenance };
}

export function normaliseOsmElementId(id: string | number): OsmElementId {
  if (typeof id === "number") {
    if (!Number.isSafeInteger(id) || id < 0) {
      throw new RangeError(`OSM numeric ID must be a non-negative safe integer: ${id}`);
    }
    return String(id);
  }
  const normalised = id.trim();
  if (normalised.length === 0) {
    throw new TypeError("OSM ID cannot be empty");
  }
  return normalised;
}

export function normaliseOsmTags(
  tags: Readonly<Record<string, unknown>> | undefined,
): OsmTags {
  if (tags === undefined) {
    return {};
  }
  const normalised: Record<string, string> = {};
  for (const [key, value] of Object.entries(tags)) {
    if (value !== null && value !== undefined) {
      normalised[key] = String(value).trim();
    }
  }
  return normalised;
}
